// src/frontend_react/src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, logoutUser, getUserStatus, verifyMfaLogin as verifyMfaLoginAPI, } from '../services/api'; // Make sure this path is correct

// Create the AuthContext
const AuthContext = createContext(null);

// Custom hook for consuming the AuthContext
export function useAuth() {
  return useContext(AuthContext);
}

// AuthProvider component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial auth check

  // Function to check authentication status (e.g., on app load or refresh)
  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getUserStatus();
      if (response.is_authenticated) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Auth status check failed:", error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array means this function is created once

  // Run on component mount to check initial auth status
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]); // Re-run if checkAuthStatus changes (it won't, but good practice)

  // Login function
  const login = useCallback(async (username, password) => {

    try {
      const response = await loginUser(username, password); // Calls POST /api/auth/login
      
      if (response.success) {
        if (response.mfa_required) {
          console.log('[AuthContext] MFA is required for user:', username);
          return { success: true, mfa_required: true, userIdForMfa: response.user_id_for_mfa_setup || response.user?.id }; // Backend should send user ID if MFA required
        } else {
          setUser(response.user);
          setIsAuthenticated(true);
          return { success: true, user: response.user };
        }
      } else {
        return { success: false, message: response.message || 'Login failed.' };
      }
    } catch (error) {
      console.error("Login API call failed:", error);
      return { success: false, message: error.message || 'Network error during login.' };
    }
  }, []); // No dependencies needed if not using internal state for MFA pending

  const verifyMfaLogin = useCallback(async (totpCode) => {
    setIsLoading(true); // Set loading at the very beginning
    try {
      const response = await verifyMfaLoginAPI(totpCode); 
      if (response.success) {
        setUser(response.user); 
        setIsAuthenticated(true);
        return { success: true, user: response.user };
      } else {
        // This is a failed attempt, but not a catastrophic error, so return the message
        return { success: false, message: response.message || "MFA verification failed." };
      }
    } catch (error) {
      console.error("MFA Verification API call failed:", error);
      // This is a network or server error
      return { success: false, message: error.message || "Network error during MFA verification." };
    } finally {
      // This block will run regardless of success or failure in the try/catch
      setIsLoading(false); 
    }
  }, []); 

  // Logout function
  const logout = useCallback(async () => {
    try {
      await logoutUser();
      setUser(null);
      setIsAuthenticated(false);
      console.log("Logged out successfully.");
    } catch (error) {
      console.error("Logout API call failed:", error);
      // Even if logout API fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // Helper to check if a user has a specific role
  const hasRole = useCallback((roleName) => {
    return user && user.roles && user.roles.includes(roleName);
  }, [user]);

  const hasPermission = useCallback((permissionName) => {
    return user && user.permissions && user.permissions.includes(permissionName);
  }, [user]);

  // Helper to check if a user has a role starting with a prefix (e.g., 'data_steward:')
  const hasRoleStartsWith = useCallback((prefix) => {
    return user && user.roles && user.roles.some(role => role.startsWith(prefix));
  }, [user]);

  // Combined helper to get implied source type (e.g., 'insurer' from 'data_steward:insurer')
  const getImpliedSourceType = useCallback(() => {
    if (user && user.roles) {
      for (const role of user.roles) {
        if (role.startsWith('data_steward:')) {
          return role.split(':')[1]; // Returns 'insurer', 'corporate', 'naluri'
        }
      }
    }
    return null;
  }, [user]);

  // The value provided to components wrapped by AuthProvider
  const authContextValue = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasRole,
    hasPermission,
    hasRoleStartsWith,
    getImpliedSourceType,
    verifyMfaLogin,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
}
